import Alpine from "alpinejs";
import htmx from "htmx.org";
import { renderRcdIcons } from "../core/icons";
import { createRcdThemeState } from "../core/theme";
import { hideRcdPreloaderWhenReady } from "../core/preloader";
import { setupRcdHtmxLifecycle } from "../core/htmx";
import { setupRcdDialogs } from "../core/components/dialog";
import { setupRcdTooltips } from "../core/components/tooltip";
import { setupRcdPopovers } from "../core/components/popover";
import { setupRcdMenus } from "../core/components/menu";
import { setupRcdSearchSelects } from "../core/components/search-select";
import { setupRcdMultiSelects } from "../core/components/multi-select";
import { setupRcdPhoneInputs } from "../core/components/phone-input";
import { setupRcdDateTimeInputs } from "../core/components/date-time-input";
import { setupRcdDropdownKits } from "../core/components/dropdown-kit";
import { setupRcdAvatars } from "../core/components/avatar";
import { setupCalendarPickers } from "../entries/admin-calendar";
import { setupRoleOrderControls, setupRolePermissionToggles } from "../entries/admin-roles";
import { createRcdUiApi, setupRcdSharedComponents, setupRcdUiStore } from "../shared/rcd-ui";

window.Alpine = Alpine;
window.htmx = htmx;

const bootUi = () => {
    renderRcdIcons();
    setupRcdSearchSelects();
    setupRcdMultiSelects(document, renderRcdIcons);
    setupRcdPhoneInputs();
    setupRcdDateTimeInputs();
    setupRcdDropdownKits(document, renderRcdIcons);
    setupRcdAvatars(document);
    setupCalendarPickers();
    setupRolePermissionToggles();
    setupRoleOrderControls();
};


const setupUiStore = () => {
    setupRcdUiStore(Alpine, bootUi);

    window.rcdAdmin = {
        ...window.rcdAdmin,
        ...createRcdUiApi(Alpine)
    };
};

window.adminApp = () => ({
    ...createRcdThemeState({
        defaultTheme: "dark",
        onChange: bootUi
    }),
    langMenuOpen: false,
    sidebarOpen: false,
    logoutLoading: false,
    init() {
        this.initTheme();

        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                this.langMenuOpen = false;
                this.sidebarOpen = false;
            }
        });
    },
    toggleLangMenu() {
        this.langMenuOpen = !this.langMenuOpen;
    },
    showDatePicker(inputId) {
        const element = document.getElementById(inputId);
        if (!element) {
            return;
        }

        if (typeof element.showPicker === "function") {
            element.showPicker();
            return;
        }

        element.focus();
    },
    async logout() {
        if (this.logoutLoading) {
            return;
        }

        this.logoutLoading = true;
        const messages = window.rcdAdminMessages || {};
        const unblock = window.rcdAdmin?.blockUi?.({ message: messages.logoutSigningOut || "Signing out" });

        try {
            const response = await fetch("/api/auth/logout", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok && response.status !== 401) {
                throw new Error("Logout failed");
            }

            window.location.assign("/login");
        } catch (error) {
            window.rcdAdmin?.toast?.({
                type: "danger",
                title: messages.logoutFailedTitle || "Logout failed",
                message: messages.logoutFailedMessage || "Please refresh the page and try again."
            });
            this.logoutLoading = false;
            unblock?.();
            bootUi();
        }
    }
});

window.emailCenterPage = () => ({
    provider: "Console",
    openTemplateEditor: false,
    templates: [],
    preview: { open: false, title: "", subject: "", html: "" },
    init(options = {}) {
        this.provider = options.provider || this.provider;
        this.openTemplateEditor = options.openTemplateEditor === true || options.openTemplateEditor === "true";

        const templateData = document.getElementById("email-template-data");
        if (templateData?.textContent) {
            try {
                this.templates = JSON.parse(templateData.textContent);
            } catch {
                this.templates = [];
            }
        }

        this.syncIcons();
    },
    previewTemplate(id) {
        const template = this.templates.find((item) => String(item.id) === String(id));
        if (!template) {
            return;
        }

        this.preview = {
            open: true,
            title: template.name,
            subject: template.subjectTemplate || template.key,
            html: template.htmlTemplate || "<p>No HTML template.</p>"
        };
        this.syncIcons();
    },
    previewDraft() {
        this.preview = {
            open: true,
            title: "Draft preview",
            subject: document.querySelector("[name='TemplateInput.SubjectTemplate']")?.value || "Draft subject",
            html: this.$refs.htmlTemplate?.value || "<p>No HTML template.</p>"
        };
        this.syncIcons();
    },
    syncIcons() {
        this.$nextTick(renderRcdIcons);
    }
});

window.rcdAdmin = {
    init() {
        bootUi();
        return true;
    }
};

document.addEventListener("DOMContentLoaded", () => {
    setupUiStore();
    setupRcdDialogs();
    setupRcdTooltips();
    setupRcdPopovers();
    setupRcdMenus();
    setupRcdSharedComponents(Alpine);
    Alpine.start();
    window.rcdAdmin.init();
    hideRcdPreloaderWhenReady();
});

setupRcdHtmxLifecycle(() => {
    bootUi();
});
